import dayjs from "dayjs";
import React from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import useServicesTable from '../hooks/logics/user-accounts/use-table-dashboard';
import { serviceStatusOptions, serviceTypeMap, statusMap } from "../pages/ServicesDashboard";
import { IGetAllServicesDocs } from "../utils/types";
import Button from "./Button";
import ConfirmationDialog from "./ConfirmationDialog";
import ViewFeedback from "./CreateService/ViewFeedback";
import DataTableView from './DataTableView2';
import DatePicker from "./DatePicker";
import Select from "./Select";

type PropTypes = {
  userId?: string;
  driverId?: string;
};

const Table = ({ userId, driverId }: PropTypes) => {
  const {
    form,
    services,
    totalPages,
    loading,
    page,
    setPage,
    setStatus,
    type,
    setType,
    fromDate,
    toDate,
    handleFromDate,
    handleToDate,
    selectedServiceId,
    setSelectedServiceId,
    selectedFeedback,
    setSelectedFeedback,
    isCancelingService,
    handleServiceCancel,
    handleApply,
    handleReset,
    isApplyButtonDisabled,
    isClearButtonDisabled,
  } = useServicesTable({ userId, driverId });

  const columns = [
    {
      header: "Date",
      className: "pl-10 ",
      accessor: (service: IGetAllServicesDocs) => (
        <p className="text-sm text-gray-700 md:pl-7 mt-[0.24rem] md:mt-0">
          {dayjs(service?.createdAt).format("DD/MM/YYYY")}
        </p>
      ),
    },
    {
      header: "Type",
      accessor: (service: IGetAllServicesDocs) => (
        <p className="text-sm text-gray-700 mt-[0.25rem] md:mt-0">
          {serviceTypeMap[service?.serviceMetaType] ?? "N/A"}
        </p>
      ),
    },
    {
      header: "Driver",
      accessor: (service: IGetAllServicesDocs) => (
        <p className="text-sm text-gray-700 mt-[0.20rem] md:mt-0">
          {service?.driver?.length === 0 ? "N/A" : service.driver[0].fullName}
        </p>
      ),
    },
    {
      header: "Status",
      accessor: (service: IGetAllServicesDocs) => (
        <p className={twMerge(
          "text-sm md:px-2 md:py-1 mt-[0.185rem] md:mt-0 rounded-full",
          service?.status === "cancelled" ? " text-red-600" :
            service?.status === "ratedByUser" ? " text-green-600" :
              " text-orange-600"
        )}>
          {statusMap(service?.status, "")}
        </p>
      ),
    },
    {
      header: "Actions",
      accessor: (service: IGetAllServicesDocs) => (
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/dashboard/orders/${service?._id}`}>
            <button className="text-xs text-primary border border-primary rounded-lg px-3 py-1 hover:bg-primary hover:text-white transition-colors">
              Details
            </button>
          </Link>
          {driverId && service?.ratingByUser && (
            <button
              onClick={() => setSelectedFeedback({
                experienceRating: service?.ratingByUser?.rating,
                feedback: service?.ratingByUser?.feedback,
                orderId: service?._id,
              })}
              className="text-xs text-primary border border-primary rounded-lg px-3 py-1 hover:bg-primary hover:text-white transition-colors"
            >
              Feedback
            </button>
          )}
          {service?.status !== "ratedByUser" && service?.status !== "cancelled" && service?.status !== "delivered" && (
            <button
              onClick={() => setSelectedServiceId(service?._id)}
              className="text-xs text-red-500 border border-red-500 rounded-lg px-3 py-1 hover:bg-red-50 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="h-full w-full mt-6 mb-4">
        <form
          onSubmit={handleApply}
          ref={form}
          className="w-full flex flex-col md:flex-row md:items-center md:justify-between md:gap-3 md:mt-5"
        >
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Orders</p>
          <div className="flex flex-col md:flex-row items-center md:justify-end mt-3 md:mt-0 space-y-3 md:space-y-0 md:w-full">
            <div className="flex items-center mr-0 md:mr-4 gap-x-2 md:gap-x-4">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-1">
                <label htmlFor="from_date" className="mr-2">
                  From:{" "}
                </label>
                <DatePicker
                  onChange={(e) => handleFromDate(e)}
                  selected={fromDate ? new Date(fromDate) : null}
                  name="fromDate"
                  id="from_date"
                  placeholderText="MM/DD/YYYY"
                  className="w-44 h-12 bg-white ml-2 px-2"
                  popperClassName='!left-10'
                  inputClassName="py-0 md:py-2 h-8 md:h-10"
                />
              </div>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-1">
                <label htmlFor="to_date" className="mr-2">
                  To:{" "}
                </label>
                <DatePicker
                  placeholderText="MM/DD/YYYY"
                  onChange={(e) => handleToDate(e)}
                  selected={toDate ? new Date(toDate) : null}
                  name="toDate"
                  id="to_date"
                  className="w-44 h-12 bg-white ml-2 px-2"
                  popperClassName='!-left-10'
                  inputClassName="py-0 md:py-2 h-8 md:h-10"
                />
              </div>
            </div>

            <div className='w-full md:w-auto flex gap-x-2 md:gap-x-4 '>
              <Select
                defaultValue=""
                onChange={(e) => setType(e.target.value)}
                value={type}
                name="type"
                placeholder="Type"
                options={[
                  { label: "Package", value: "package_pickup_meta" },
                  { label: "Curb", value: "curbside_pickup_meta" },
                  { label: "In store", value: "instore_pickup_meta" },
                ]}
                className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
                containerClassName='w-1/2 md:w-auto'
              />

              <Select
                defaultValue=""
                onChange={(e) => setStatus(e.target.value)}
                // className="w-24 outline-none bg-white h-10 pl-4 pr-2 "
                className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
                containerClassName='w-1/2 md:w-auto'
                placeholder="Status"
                options={serviceStatusOptions}
              />
            </div>
            <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4 mb-8 md:mb-0 md:ml-4">
              <Button
                type="submit"
                className="flex-1 md:flex-none md:w-28 min-h-0 h-8 md:h-10"
                disabled={isApplyButtonDisabled()}
              >
                Apply
              </Button>

              <Button
                type="button"
                disabled={isClearButtonDisabled()}
                onClick={handleReset}
                className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10"
              >
                Clear
              </Button>
            </div>
          </div>
        </form>

        <div className="mt-4">
          <DataTableView
            data={services}
            columns={columns}
            isFetching={loading}
            emptyStateMessage="No Data Found"
            currentPage={page}
            setCurrentPage={setPage}
            totalPages={totalPages}
            headerClassName="px-4 py-4 h-[50px] rounded-t-md"
          />
        </div>
      </div>

      <ViewFeedback
        feedback={selectedFeedback}
        screen="driverDetails"
        isVisible={selectedFeedback.orderId !== ""}
        setVisible={() => setSelectedFeedback({ experienceRating: null, feedback: "", orderId: "" })}
      />
      <ConfirmationDialog
        isSubmitButtonDisabled={isCancelingService}
        handleClick={handleServiceCancel}
        isVisible={selectedServiceId !== ""}
        onHide={() => setSelectedServiceId("")}
        text="Are you sure you want to cancel this service?"
      />
    </>
  );
};

export default Table;